import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";

import { FilterBar, Input, SortDropdown, SortDropdownValue } from "@/modules/common/components";
import { MagnifyingGlassIcon } from "@/modules/common/assets/icons";

import FilterButton from "./FilterButton";
import FilterModal from "./FilterModal";
import { FilterFieldConfig } from "./types";
import { UseFiltersResult } from "./useFilters";

interface EntityFilterBarProps<TFilters extends { search?: string }, TSortField extends string> {
  filters: UseFiltersResult<TFilters>;
  sort: SortDropdownValue<TSortField> | null;
  onSortChange: (sort: SortDropdownValue<TSortField> | null) => void;
  sortOptions: { field: TSortField; label: string }[];
  filterFields: FilterFieldConfig[];
  modalTitle: string;
  searchPlaceholder?: string;
  /** Campos de filtro extras exibidos apenas no desktop, entre a busca e o botão de filtros */
  children?: React.ReactNode;
}

const EntityFilterBar = <TFilters extends { search?: string }, TSortField extends string>({
  filters,
  sort,
  onSortChange,
  sortOptions,
  filterFields,
  modalTitle,
  searchPlaceholder = "Pesquisar",
  children,
}: EntityFilterBarProps<TFilters, TSortField>) => {
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [search, setSearch] = useState(filters.values.search ?? "");
  const [debouncedSearch] = useDebounce(search, 500);

  useEffect(() => {
    setSearch(filters.values.search ?? "");
  }, [filters.values.search]);

  useEffect(() => {
    if (debouncedSearch === (filters.values.search ?? "")) return;
    filters.setValue("search", (debouncedSearch || undefined) as TFilters["search"]);
  }, [debouncedSearch]);

  return (
    <FilterBar>
      <FilterBar.Mobile>
        <div className="flex-1 min-w-0">
          <Input
            className="h-10"
            placeholder={searchPlaceholder}
            icon={<MagnifyingGlassIcon />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <FilterButton appliedCount={filters.appliedCount} onClick={() => setIsFilterModalOpen(true)} className="sm:hidden" />

        <div className="sm:hidden">
          <SortDropdown options={sortOptions} value={sort} onChange={onSortChange} />
        </div>
      </FilterBar.Mobile>

      <FilterBar.Desktop className="items-center">
        {children}

        <FilterButton appliedCount={filters.appliedCount} onClick={() => setIsFilterModalOpen(true)} className="hidden sm:flex" />
        <SortDropdown options={sortOptions} value={sort} onChange={onSortChange} />
      </FilterBar.Desktop>

      <FilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        title={modalTitle}
        fields={filterFields}
        values={filters.values}
        onApply={(patch) => filters.setValues(patch)}
        onClear={filters.clear}
      />
    </FilterBar>
  );
};

export default EntityFilterBar;
